import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';

export default function Button({
  children,
  to,
  href,
  onClick,
  variant = 'primary',
  size = 'md',
  showArrow = false,
  arrowType = 'diagonal', // 'diagonal' | 'right'
  className = '',
  type = 'button',
  disabled = false
}) {
  const baseStyles = "inline-flex flex-row items-center justify-center whitespace-nowrap font-medium transition-all duration-300 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 select-none group";

  const sizeStyles = {
    xs: "text-[11px] px-2.5 py-1.5 gap-1",
    sm: "text-xs px-4 py-2 gap-2",
    md: "text-sm px-6 py-3 gap-2.5 tracking-wide",
    lg: "text-base px-7 py-3.5 gap-2.5 tracking-wide",
  };

  const variantStyles = {
    primary: "bg-[#0F766E] hover:bg-[#123B5D] text-white shadow-sm hover:shadow-md focus:ring-[#0F766E] border border-[#0F766E] hover:translate-y-[-1px]",
    secondary: "bg-white hover:bg-[#0F766E] text-[#0F766E] hover:text-white focus:ring-[#0F766E] border border-[#0F766E] hover:translate-y-[-1px]",
    outline: "bg-transparent hover:bg-[#0F766E] text-[#0F766E] hover:text-white border border-[#0F766E]/40 hover:border-[#0F766E] focus:ring-[#0F766E] hover:translate-y-[-1px]",
    gold: "bg-[#2A9D8F] hover:bg-[#0F766E] text-white font-semibold shadow-sm hover:shadow focus:ring-[#2A9D8F] border border-[#2A9D8F] hover:translate-y-[-1px]",
    ghost: "bg-transparent hover:bg-[#F5FAF9] text-[#0F766E] underline-offset-4 hover:underline",
    white: "bg-white hover:bg-[#F5FAF9] text-[#123B5D] hover:text-[#0F766E] shadow-sm hover:shadow-md border border-[#D9E8E5] hover:translate-y-[-1px]",
    darkOutline: "bg-transparent hover:bg-white/10 text-white border border-white/40 hover:border-white focus:ring-white",
  };

  const combinedClasses = `${baseStyles} ${sizeStyles[size] || sizeStyles.md} ${variantStyles[variant] || variantStyles.primary} ${disabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''} ${className}`;

  const renderArrow = () => {
    if (!showArrow) return null;
    const arrowSize = size === 'xs' ? 'w-3 h-3' : 'w-4 h-4';
    return arrowType === 'diagonal' ? (
      <ArrowUpRight className={`${arrowSize} flex-shrink-0 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5`} />
    ) : (
      <ArrowRight className={`${arrowSize} flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1`} />
    );
  };

  if (to) {
    return (
      <Link to={to} className={combinedClasses}>
        <span className="inline-flex flex-row items-center gap-2 whitespace-nowrap flex-nowrap">{children}</span>
        {renderArrow()}
      </Link>
    );
  }

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={combinedClasses}>
        <span className="inline-flex flex-row items-center gap-2 whitespace-nowrap flex-nowrap">{children}</span>
        {renderArrow()}
      </a>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} className={combinedClasses}>
      <span className="inline-flex flex-row items-center gap-2 whitespace-nowrap flex-nowrap">{children}</span>
      {renderArrow()}
    </button>
  );
}
